import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

const painPointsData = {
  en: [
    { text: 'Hundreds of vehicle models', icon: 'ri-car-line' },
    { text: 'New, pre-owned or demo', icon: 'ri-price-tag-3-line' },
    { text: 'Finance, lease or purchase', icon: 'ri-bank-card-line' },
    { text: 'Electric & Hybrid Technology', icon: 'ri-flashlight-line' },
    { text: 'Thousands of trims &amp; options', icon: 'ri-settings-3-line' },
    { text: 'Complex incentives & rebates', icon: 'ri-coupon-line' },
    { text: 'Federal and Provincial Subsidies', icon: 'ri-government-line' },
    { text: 'Persuasive marketing', icon: 'ri-megaphone-line' },
    { text: 'Conflicting reviews & ratings', icon: 'ri-star-half-line' },
    { text: 'Dealership pressure & tactics', icon: 'ri-user-voice-line' },
    { text: 'Time-consuming research', icon: 'ri-time-line' },
    { text: 'Uncertain resale & depreciation', icon: 'ri-line-chart-line' },
    { text: 'Hidden fees & extra costs', icon: 'ri-money-dollar-circle-line' },
    { text: 'Surprise insurance costs', icon: 'ri-shield-line' },
  ],
  fr: [
    { text: 'Des centaines de modèles', icon: 'ri-car-line' },
    { text: 'Neuf, occasion ou démo', icon: 'ri-price-tag-3-line' },
    { text: 'Financement, location, achat', icon: 'ri-bank-card-line' },
    { text: 'Technologie électrique & hybride', icon: 'ri-flashlight-line' },
    { text: 'Des milliers de versions', icon: 'ri-settings-3-line' },
    { text: 'Incitatifs & rabais complexes', icon: 'ri-coupon-line' },
    { text: 'Subventions fédérales & provinciales', icon: 'ri-government-line' },
    { text: 'Marketing persuasif', icon: 'ri-megaphone-line' },
    { text: 'Avis contradictoires', icon: 'ri-star-half-line' },
    { text: 'Pression des concessionnaires', icon: 'ri-user-voice-line' },
    { text: 'Recherches exhaustives', icon: 'ri-time-line' },
    { text: 'Valeur de revente incertaine', icon: 'ri-line-chart-line' },
    { text: 'Frais cachés & coûts extra', icon: 'ri-money-dollar-circle-line' },
    { text: "Coûts d'assurance", icon: 'ri-shield-line' },
  ]
};

function PainPill({ text, icon, delay = 0 }: { text: string; icon: string; delay?: number }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative flex items-center gap-2.5 px-4 py-2.5 rounded-full cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(196,168,130,0.12)' : 'rgba(196,168,130,0.06)',
        border: '1px solid rgba(196,168,130,0.2)',
        backdropFilter: 'blur(8px)',
        opacity: visible ? 1 : 0,
        transform: visible
          ? hovered ? 'translateY(-5px) scale(1.03)' : 'translateY(0) scale(1)'
          : 'translateY(12px) scale(0.96)',
        transition: visible
          ? hovered
            ? `opacity 0.3s ease ${delay}ms, transform 0.18s cubic-bezier(.22,1,.36,1)`
            : `opacity 0.4s ease ${delay}ms, transform 0.38s cubic-bezier(.22,1,.36,1)`
          : `opacity 0.4s ease ${delay}ms, transform 0.4s cubic-bezier(.22,1,.36,1) ${delay}ms`,
        willChange: 'transform',
      }}
    >
      <div className="relative z-10 w-4 h-4 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-sm`} style={{ color: '#c4a882' }}></i>
      </div>
      <span className="relative z-10 text-xs sm:text-sm font-medium whitespace-nowrap leading-snug" style={{ color: 'rgba(232,216,192,0.8)' }}>
        {text}
      </span>
    </div>
  );
}

function CyclingPainPoints({ painPoints }: { painPoints: { text: string; icon: string }[] }) {
  const doubled = [...painPoints, ...painPoints];

  return (
    <div className="max-w-full mb-5 sm:mb-8 overflow-hidden" style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)' }}>
      <style>{`
        @keyframes marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          display: flex;
          gap: 10px;
          width: max-content;
          animation: marquee-scroll 32s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
      <div className="marquee-track py-1">
        {doubled.map((item, i) => (
          <div
            key={i}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-full flex-shrink-0"
            style={{
              background: 'rgba(196,168,130,0.06)',
              border: '1px solid rgba(196,168,130,0.2)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`${item.icon} text-sm`} style={{ color: '#c4a882' }}></i>
            </div>
            <span className="text-xs sm:text-sm font-medium whitespace-nowrap leading-snug" style={{ color: 'rgba(232,216,192,0.8)' }}>
              {item.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProblemSection() {
  const { lang } = useLanguage();
  const [diffVisible, setDiffVisible] = useState(false);
  const [diffHovered, setDiffHovered] = useState(false);
  const diffRef = useRef<HTMLDivElement>(null);

  const painPoints = painPointsData[lang];

  const content = {
    en: {
      headline1: 'In a sea of options, which one is truly right for you?',
      diffLabel: 'The Automatchlab Difference',
      diffLine1: 'Most websites help dealerships',
      diffLine1Bold: 'sell',
      diffLine1End: 'vehicles.',
      diffLine2: 'Automatchlab helps',
      diffLine2Bold: 'you',
      diffLine2End: 'choose the right one.'
    },
    fr: {
      headline1: 'Face à tant de choix, lequel est le bon pour vous\u00a0?',
      diffLabel: 'La Différence Automatchlab',
      diffLine1: 'La plupart des sites aident les concessionnaires à',
      diffLine1Bold: 'vendre',
      diffLine1End: 'des véhicules.',
      diffLine2: 'Automatchlab',
      diffLine2Bold: 'vous',
      diffLine2End: 'aide à choisir le bon.'
    }
  };

  const t = content[lang];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setDiffVisible(true); },
      { threshold: 0.25 }
    );
    if (diffRef.current) observer.observe(diffRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative pt-0 pb-6 sm:pb-10 overflow-hidden" style={{ background: '#08080f' }}>
      <style>{`
        @keyframes shimmer-problem {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Topographic background texture */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 1 }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(196,168,130,0.04) 0%, transparent 60%)' }} />

      <div className="relative z-10 pt-4 sm:pt-8">
        {/* Headline */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20 mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-3xl lg:text-5xl font-bold leading-tight text-center" style={{ fontFamily: 'Satoshi, Inter, sans-serif', color: 'rgba(232,216,192,0.9)' }}>
            {t.headline1}
          </h2>
        </div>

        <CyclingPainPoints painPoints={painPoints} />

        {/* Key statement card */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
          <div
            ref={diffRef}
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden p-5 sm:p-8 lg:p-10 cursor-default"
            onMouseEnter={() => setDiffHovered(true)}
            onMouseLeave={() => setDiffHovered(false)}
            style={{
              background: 'linear-gradient(135deg, rgba(14,14,26,0.95) 0%, rgba(8,8,18,0.98) 100%)',
              border: '1px solid rgba(196,168,130,0.18)',
              opacity: diffVisible ? 1 : 0,
              transform: diffVisible
                ? diffHovered ? 'translateY(-6px) scale(1.012)' : 'translateY(0) scale(1)'
                : 'translateY(60px) scale(0.95)',
              transition: diffHovered
                ? 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.18s cubic-bezier(.22,1,.36,1)'
                : 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.42s cubic-bezier(.22,1,.36,1)',
              willChange: 'transform',
              boxShadow: '0 0 0 1px rgba(196,168,130,0.08), 0 12px 48px rgba(0,0,0,0.5)',
            }}
          >
            {/* Inner glow */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 20% 50%, rgba(196,168,130,0.06) 0%, transparent 60%)' }} />
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.4), transparent)' }} />
            <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #c4a882 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

            <div className="relative z-10 flex flex-row items-center justify-between gap-4 sm:gap-8">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-bold tracking-[0.2em] uppercase mb-2 sm:mb-3" style={{ background: 'linear-gradient(90deg, #8a7560 0%, #c4a882 40%, #e8d8c0 55%, #c4a882 70%, #8a7560 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmer-problem 3s linear infinite' }}>
                  {t.diffLabel}
                </p>
                <p className="text-sm sm:text-xl lg:text-3xl font-light mb-2 sm:mb-3 leading-snug" style={{ color: 'rgba(232,216,192,0.6)', opacity: diffVisible ? 1 : 0, transform: diffVisible ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity 0.6s ease-out 0.35s, transform 0.6s ease-out 0.35s' }}>
                  {t.diffLine1} <span style={{ color: '#e8d8c0', fontWeight: 600 }}>{t.diffLine1Bold}</span> {t.diffLine1End}
                </p>
                <p className="text-sm sm:text-xl lg:text-3xl font-bold leading-snug text-white" style={{ opacity: diffVisible ? 1 : 0, transform: diffVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out 0.5s, transform 0.6s ease-out 0.5s' }}>
                  {t.diffLine2} <span style={{ color: '#c4a882' }}>{t.diffLine2Bold}</span> {t.diffLine2End}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0" style={{ opacity: diffVisible ? 1 : 0, transform: diffVisible ? 'scale(1)' : 'scale(0.5)', transition: 'opacity 0.7s ease-out 0.6s, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s' }}>
                <div className="w-12 h-12 sm:w-20 sm:h-20 flex items-center justify-center rounded-xl sm:rounded-2xl" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.22)' }}>
                  <i className="ri-shield-check-line text-2xl sm:text-4xl text-[#c4a882]"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
